<script setup lang="ts">
    import { computed } from 'vue';
    import { parseDate, today, getLocalTimeZone } from '@internationalized/date';
    import type { ChangedFields, Company, Employee } from '@/types';
    import EmployeeChangesGroup from '../EmployeeChangesGroup.vue';
    import { useI18n } from 'vue-i18n';
    import { formatDate, formatCurrency } from '@/utils/helpers';

    const { t } = useI18n();

    const props = defineProps<{
        employee: Employee;
        data: ChangedFields;
        optional?: ChangedFields;
        company?: Company;
    }>();

    const alert = computed(() => {
        if (props.employee?.unnoted_salary) {
            return t('employee.changes.past_salary', {
                amount: formatCurrency(props.employee.unnoted_salary.amount),
                date: formatDate(props.employee.unnoted_salary.starts_at),
            });
        }
    });

    const optionalFields = [
        'has_lunch_arrangement',
        'has_fixed_add_ons',
        'has_free_phone',
        'has_free_car',
        'danmarks_statistik',
        'health_insurance',
        'kilometer',
        'has_pension',
        'diaeter',
    ];

    const values = computed(() => {
        return [
            ...Object.entries(props.data?.attributes ?? {}).map(([key, value]) => {
                if (optionalFields.includes(key)) {
                    return {
                        name: key,
                        group: 'optional',
                        key: key,
                        oldValue: props.data?.old?.[key]?.value ? t('forms.yes') : t('forms.no'),
                        realValue: props.employee.optional?.[key] ? t('forms.yes') : t('forms.no'),
                        translation: t(`employee.changed_fields.${key}`),
                    };
                }
                return {
                    name: key,
                    group: key,
                    key: key,
                    oldValue: props.data?.old?.[key]?.value,
                    realValue: props.data?.attributes?.[key]?.value,
                    updateVal: props.data?.attributes?.[key]?.updated_at,
                    translation: t(`employee.changed_fields.${key}`),
                };
            }),
        ];
    });
</script>

<template>
    <EmployeeChangesGroup
        :data="data"
        :values="values"
        :employee="employee"
        :alert="alert"
    />
</template>
